<!--
	The header of this build carries the section switcher and nothing else of its own. The pages
	navigation — Projects and the rest — is NOT projected here: it moves down a level with the
	sections, and the header's `[headerNav]` slot stays empty rather than being filled with a copy
	that would have to be removed again. `ds-navigation` is therefore absent from this build's graph
	entirely, the same way the rail is absent from the other one.
-->
<!--
	The title names the open project (or the Platform Administrator group) in the band's centre
	(EFF-26168): in this build the project subheader no longer carries it. The other build keeps it there
	and projects no title.
-->
<ds-header class="ds-platform-shell__header">
	<eff-app-section-switcher #sectionSwitcher headerSections dsTestId="platform-shell-section-switcher" />
	<ds-header-title headerTitle vertical="center" class="ds-platform-shell__header-title" [title]="headerTitle()" />
	<div headerEnd vertical="center" class="shrink-0">
		<ng-template [cdkPortalOutlet]="topBarPortal()" />
	</div>
</ds-header>

<div class="ds-flex eff-flex--full ds-platform-shell__body">
	<!--
		The rail is a SIBLING of the shell below, not a child of its aside: the aside is a vertical
		stack with pinned header and footer slots, and the rail has to outlive it.
	-->
	<!--
		The user menu is COMPOSED into the rail's footer slot, not configured through the rail: the rail
		takes no inputs by contract, and the menu reads the session, the permission ladder and the
		billing config — none of which a `type:ui` library may know about (EFF-26081).
	-->
	<eff-app-rail #rail class="ds-platform-shell__rail" dsTestId="platform-shell-rail">
		<ds-user-menu effAppRailFooter />
	</eff-app-rail>

	<!--
		Right after the rail, so the chevron it draws over the rail's centre comes after the rail's own
		controls in focus order, and the panel it opens comes right after the chevron.
	-->
	<!-- The open navigator covers the rail, so it carries the user menu in its own footer, with the name. -->
	<ds-domain-navigator>
		<ng-template #footer>
			<ds-user-menu withName />
		</ng-template>
	</ds-domain-navigator>

	<!--
		Overlay mode, and it sits BEFORE the shell on purpose: the block is absolutely positioned, so DOM
		order is free to match the visual order the user perceives — panel first, then the content it
		covers. It also carries the landmark and the name the kit's aside contributes in push mode, so the
		panel is the same region to assistive technology in both modes rather than an anonymous div in one
		of them.
	-->
	@if (isPanelOpen() && panelRenderMode() === APP_SHELL_PANEL_MODE.Overlay) {
		<div
			#overlayPanel
			class="ds-platform-shell__overlay-panel"
			dsTestId="platform-shell-overlay-panel"
			role="complementary"
			aria-label="Section panel"
			[style.--ds-platform-shell-panel-width]="panelWidth()"
		>
			<eff-app-panel-tabs class="slds-shrink-none" />

			<div class="ds-platform-shell__overlay-body">
				<eff-app-side-panel />
			</div>
		</div>
	}

	<eff-sidebar-shell
		ariaLabel="Section panel"
		class="eff-flex--full ds-platform-shell__content"
		[asideWidth]="panelWidth()"
		[collapsible]="false"
	>
		<!--
			Closed is the ABSENCE of the aside rather than a narrowed one — which is also what keeps
			a closed panel out of the accessibility tree with no `inert` attribute. Push mode is the
			only mode that ever projects the aside; overlay mode leaves it unprojected and renders the
			panel through the sibling container below instead. The reasoning in full is on the
			component class.
		-->
		@if (isPanelOpen() && panelRenderMode() === APP_SHELL_PANEL_MODE.Push) {
			<!--
				No divider against main: the kit draws it on the aside HOST, so it would run the
				pane's full height and cut through the chrome band that spans both columns. The
				rail already carries the only vertical edge this region needs, on its far side.
			-->
			<eff-sidebar-shell-aside class="ds-platform-shell__aside" [divider]="false">
				<!--
					The aside half of the chrome band, present only while the page registers panel tabs. In
					the kit's PINNED top slot, so it stays put while the panel body scrolls under it — and so
					it lines up with the tab strip across the column boundary.
				-->
				@if (hasPanelTabs()) {
					<eff-sidebar-shell-aside-header>
						<eff-app-panel-tabs />
					</eff-sidebar-shell-aside-header>
				}

				<eff-app-side-panel />
			</eff-sidebar-shell-aside>
		}

		<eff-sidebar-shell-main effClarityFlyInMaxWidth class="ds-platform-shell__main">
			<!--
				The main half of the chrome band, and the reason it is HERE rather than above the
				body row: the tabs belong over main, so opening and closing the panel must not slide
				them sideways. Main is the page scroll-host, so both strips are pinned to its top edge
				as ONE block from the stylesheet — pinned separately they would stack on the same edge
				and the second would cover the first.
			-->
			<!-- The white page starts at this band's bottom edge, and so does every Clarity fly-in. -->
			<div effClarityFlyInTopEdge class="slds-shrink-none ds-platform-shell__tabs">
				<eff-app-tab-strip class="ds-platform-shell__section-tabs" />

				<!--
					The Platform Administrator row (EFF-24841), which used to render from `PagesComponent`.
					It is section chrome, so it belongs to the shell that has sections: the build without
					them reaches those four pages from the header navigation, and a second row of the same
					entries under the header is a duplicate. Still inside main and still in the content
					column, which is where the design puts it — only the file it is written in changed.
					It is empty, and so renders nothing at all, on every URL outside that group.
				-->
				<eff-app-tab-strip class="ds-platform-shell__platform-admin-tabs" [tabs]="platformAdminTabs()" />
			</div>

			<router-outlet />
		</eff-sidebar-shell-main>
	</eff-sidebar-shell>

	<ds-sidenav-wrapper
		class="ds-platform-shell__extra-details"
		[style.--ds-platform-shell-extra-details-start]="extraDetailsStart()"
	/>
</div>
